import React, { useState } from 'react';
import { auth } from '../api';

const FEATURES = [
  { icon: '◎', label: 'Add any RSS feed', sub: 'Any site, any topic, your choice' },
  { icon: '⬡', label: 'ANIMA synthesizes it', sub: 'Cross-source patterns, not just summaries' },
  { icon: '◈', label: 'Brief delivered daily', sub: 'Inbox or in-app, your schedule' },
  { icon: '◇', label: 'No algorithm. No ads.', sub: 'Sovereign by design' },
];

const SOURCES = ['Hacker News', 'Reuters', 'ZeroHedge', 'NASA', 'The Intercept', 'CoinDesk', 'Ars Technica', 'Quanta Mag'];

const AuthScreen = ({ onLogin }) => {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fn = mode === 'login' ? auth.login : auth.register;
      const res = await fn({ email, password });
      onLogin(res.data.user, res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      {/* Background */}
      <div style={styles.grid} />
      <div style={styles.orb1} />
      <div style={styles.orb2} />
      <div style={styles.orb3} />

      <div style={styles.layout}>

        {/* LEFT — Landing content */}
        <div style={styles.left}>

          {/* Logo */}
          <div style={styles.logoRow}>
            <div style={styles.logoMark}>V</div>
            <div>
              <div style={styles.logoText}>VERABRIEF</div>
              <div style={styles.logoTagline}>Sovereign Intelligence. Daily Clarity.</div>
            </div>
            <div style={styles.animaBadge}>
              <span style={styles.animaDot} />
              <span style={styles.animaLabel}>ANIMA INSIDE</span>
            </div>
          </div>

          {/* Headline */}
          <div style={styles.headline}>
            <h1 style={styles.h1}>
              Stop scrolling.<br />
              <span style={styles.h1Accent}>Start knowing.</span>
            </h1>
            <p style={styles.subline}>
              VERABRIEF pulls the latest articles from RSS feeds you choose,
              and ANIMA synthesizes them into a clean daily brief — delivered to your inbox every morning.
              No algorithm. No ads. No noise.
            </p>
          </div>

          {/* Feature pills */}
          <div style={styles.features}>
            {FEATURES.map((f, i) => (
              <div key={i} style={styles.featureItem}>
                <span style={styles.featureIcon}>{f.icon}</span>
                <div>
                  <div style={styles.featureLabel}>{f.label}</div>
                  <div style={styles.featureSub}>{f.sub}</div>
                </div>
              </div>
            ))}
          </div>

          {/* RSS explainer */}
          <div style={styles.rssBox}>
            <div style={styles.rssTitle}>What is an RSS feed?</div>
            <div style={styles.rssBody}>
              RSS is a direct subscription to any website's content — no social media middleman,
              no algorithm filtering what you see. Every major news outlet, blog, and publication has one.
              VERABRIEF reads them. ANIMA synthesizes them.
            </div>
          </div>

          {/* Scrolling source ticker */}
          <div style={styles.tickerWrap}>
            <div style={styles.tickerLabel}>42 curated sources in Discover →</div>
            <div style={styles.ticker}>
              {[...SOURCES, ...SOURCES].map((s, i) => (
                <span key={i} style={styles.tickerItem}>{s}</span>
              ))}
            </div>
          </div>

          {/* How it works — compact */}
          <div style={styles.howWrap}>
            <div style={styles.howTitle}>HOW IT WORKS</div>
            <div style={styles.howSteps}>
              {[
                { n: '1', t: 'Add sources', b: 'Pick RSS feeds from the Discover tab or add your own' },
                { n: '2', t: 'Generate brief', b: 'ANIMA reads the latest articles across all your sources' },
                { n: '3', t: 'Read or receive', b: 'In-app instantly or delivered to your inbox each morning' },
              ].map((s, i) => (
                <div key={i} style={styles.howStep}>
                  <div style={styles.howNum}>{s.n}</div>
                  <div>
                    <div style={styles.howStepTitle}>{s.t}</div>
                    <div style={styles.howStepBody}>{s.b}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing */}
          <div style={styles.pricing}>
            <div style={styles.pricingTier}>
              <div style={styles.pricingTierName}>FREE</div>
              <div style={styles.pricingTierPrice}>$0</div>
              <div style={styles.pricingTierFeatures}>
                <div>3 RSS sources</div>
                <div>1 brief per day</div>
                <div>Email delivery</div>
                <div>Discover tab</div>
              </div>
            </div>
            <div style={styles.pricingDivider} />
            <div style={styles.pricingTier}>
              <div style={{ ...styles.pricingTierName, color: '#00d4ff' }}>PREMIUM</div>
              <div style={{ ...styles.pricingTierPrice, color: '#00d4ff' }}>$4.95<span style={styles.pricingMo}>/mo</span></div>
              <div style={styles.pricingTierFeatures}>
                <div>Unlimited sources</div>
                <div>Unlimited briefs</div>
                <div>ANIMA cross-source analysis</div>
                <div>SMS delivery <span style={styles.soon}>soon</span></div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={styles.leftFooter}>
            Built on <span style={styles.footerAccent}>ANIMACORE</span> · Powered by <span style={styles.footerAccent}>ANIMA</span> · No ads, ever.
          </div>

        </div>

        {/* RIGHT — Auth card */}
        <div style={styles.right}>
          <div style={styles.authCard}>

            <div style={styles.authLogoWrap}>
              <div style={styles.authLogoMark}>V</div>
              <div>
                <div style={styles.authLogoText}>VERABRIEF</div>
                <div style={styles.authLogoSub}>Sovereign Intelligence. Daily Clarity.</div>
              </div>
            </div>

            <div style={styles.authAnimaBadge}>
              <span style={styles.animaDot} />
              <span style={styles.animaLabel}>ANIMA INSIDE</span>
            </div>

            <div style={styles.tabs}>
              <button
                style={{ ...styles.tab, ...(mode === 'login' ? styles.tabActive : {}) }}
                onClick={() => { setMode('login'); setError(''); }}
              >
                Sign In
              </button>
              <button
                style={{ ...styles.tab, ...(mode === 'register' ? styles.tabActive : {}) }}
                onClick={() => { setMode('register'); setError(''); }}
              >
                Create Account
              </button>
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && <div style={styles.errorMsg}>{error}</div>}

              <button
                type="submit"
                className="btn btn-primary"
                style={styles.submitBtn}
                disabled={loading}
              >
                {loading ? 'Loading...' : mode === 'login' ? 'Access Your Brief' : 'Create Account'}
              </button>
            </form>

            <p style={styles.authFooter}>
              Your intelligence feed. Curated by you. Synthesized by ANIMA.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
  },
  grid: {
    position: 'fixed',
    inset: 0,
    backgroundImage: `
      linear-gradient(rgba(0, 212, 255, 0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 212, 255, 0.025) 1px, transparent 1px)
    `,
    backgroundSize: '48px 48px',
    pointerEvents: 'none',
  },
  orb1: {
    position: 'fixed',
    top: '-15%',
    right: '30%',
    width: 700,
    height: 700,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,212,255,0.05) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  orb2: {
    position: 'fixed',
    bottom: '-20%',
    left: '-5%',
    width: 500,
    height: 500,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(123,97,255,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  orb3: {
    position: 'fixed',
    top: '40%',
    right: '-10%',
    width: 400,
    height: 400,
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(0,255,170,0.03) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  layout: {
    display: 'flex',
    minHeight: '100vh',
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  left: {
    flex: '1 1 500px',
    padding: '48px 48px 48px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
    maxWidth: 660,
    overflowY: 'auto',
  },
  right: {
    flex: '0 0 420px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    borderLeft: '1px solid var(--border)',
    background: 'rgba(10, 10, 15, 0.6)',
    backdropFilter: 'blur(12px)',
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  logoMark: {
    width: 44,
    height: 44,
    borderRadius: 12,
    background: 'var(--primary-dim)',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    fontWeight: 700,
    color: 'var(--primary)',
    flexShrink: 0,
  },
  logoText: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: 'var(--text)',
  },
  logoTagline: {
    fontSize: 10,
    color: 'var(--text-dim)',
    letterSpacing: '0.04em',
    marginTop: 2,
  },
  animaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'var(--anima-dim)',
    border: '1px solid rgba(123, 97, 255, 0.25)',
    borderRadius: 20,
    padding: '3px 10px',
    marginLeft: 'auto',
  },
  animaDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--anima)',
    display: 'inline-block',
    boxShadow: '0 0 6px var(--anima)',
    animation: 'pulse-glow 2s infinite',
  },
  animaLabel: {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.12em',
    color: 'var(--anima)',
  },
  headline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  h1: {
    fontSize: 52,
    fontWeight: 800,
    letterSpacing: '-0.03em',
    lineHeight: 1.05,
    color: 'var(--text)',
    margin: 0,
  },
  h1Accent: {
    color: '#00d4ff',
  },
  subline: {
    fontSize: 15,
    color: 'var(--text-secondary)',
    lineHeight: 1.75,
    maxWidth: 520,
    margin: 0,
  },
  features: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  featureItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '14px',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
  },
  featureIcon: {
    fontSize: 18,
    color: '#00d4ff',
    flexShrink: 0,
    marginTop: 1,
  },
  featureLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--text)',
  },
  featureSub: {
    fontSize: 11,
    color: 'var(--text-dim)',
    marginTop: 2,
    lineHeight: 1.5,
  },
  rssBox: {
    background: 'rgba(0, 212, 255, 0.04)',
    border: '1px solid rgba(0, 212, 255, 0.15)',
    borderRadius: 12,
    padding: '16px 20px',
  },
  rssTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: '#00d4ff',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  rssBody: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    lineHeight: 1.7,
  },
  tickerWrap: {
    overflow: 'hidden',
  },
  tickerLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: 'var(--text-dim)',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  ticker: {
    display: 'flex',
    gap: 12,
    flexWrap: 'wrap',
  },
  tickerItem: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: '4px 12px',
    letterSpacing: '0.04em',
  },
  howWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  howTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--text-dim)',
  },
  howSteps: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  howStep: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  howNum: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'var(--primary-dim)',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    color: '#00d4ff',
    fontSize: 12,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  howStepTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: 'var(--text)',
  },
  howStepBody: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    marginTop: 2,
    lineHeight: 1.6,
  },
  pricing: {
    display: 'flex',
    gap: 0,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 16,
    overflow: 'hidden',
  },
  pricingTier: {
    flex: 1,
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  pricingDivider: {
    width: 1,
    background: 'var(--border)',
  },
  pricingTierName: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    color: 'var(--text-dim)',
  },
  pricingTierPrice: {
    fontSize: 28,
    fontWeight: 800,
    color: 'var(--text)',
    letterSpacing: '-0.02em',
    lineHeight: 1,
    marginBottom: 6,
  },
  pricingMo: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  pricingTierFeatures: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    fontSize: 12,
    color: 'var(--text-secondary)',
    lineHeight: 1.6,
  },
  soon: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.08em',
    color: '#7b61ff',
    background: 'rgba(123, 97, 255, 0.15)',
    borderRadius: 4,
    padding: '1px 5px',
    marginLeft: 4,
  },
  leftFooter: {
    fontSize: 11,
    color: 'var(--text-dim)',
    letterSpacing: '0.04em',
    paddingTop: 8,
    borderTop: '1px solid var(--border)',
  },
  footerAccent: {
    color: 'var(--text-secondary)',
    fontWeight: 600,
  },

  // Auth card
  authCard: {
    width: '100%',
    maxWidth: 380,
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 20,
    padding: 36,
    position: 'relative',
    animation: 'fadeIn 0.5s ease forwards',
  },
  authLogoWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    marginBottom: 20,
  },
  authLogoMark: {
    width: 48,
    height: 48,
    borderRadius: 13,
    background: 'var(--primary-dim)',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--primary)',
    flexShrink: 0,
  },
  authLogoText: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: '0.15em',
    color: 'var(--text)',
  },
  authLogoSub: {
    fontSize: 10,
    color: 'var(--text-secondary)',
    letterSpacing: '0.04em',
    marginTop: 3,
  },
  authAnimaBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    background: 'var(--anima-dim)',
    border: '1px solid rgba(123, 97, 255, 0.25)',
    borderRadius: 20,
    padding: '4px 10px',
    marginBottom: 24,
  },
  tabs: {
    display: 'flex',
    gap: 4,
    background: 'var(--surface2)',
    borderRadius: 10,
    padding: 4,
    marginBottom: 24,
  },
  tab: {
    flex: 1,
    padding: '9px 0',
    borderRadius: 7,
    border: 'none',
    background: 'transparent',
    color: 'var(--text-secondary)',
    fontSize: 13,
    fontWeight: 600,
    fontFamily: 'Space Grotesk, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.2s',
    letterSpacing: '0.02em',
  },
  tabActive: {
    background: 'var(--surface)',
    color: 'var(--text)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  errorMsg: {
    background: 'rgba(255, 77, 109, 0.1)',
    border: '1px solid rgba(255, 77, 109, 0.3)',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    color: 'var(--danger)',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: '0.04em',
    marginTop: 4,
  },
  authFooter: {
    textAlign: 'center',
    fontSize: 11,
    color: 'var(--text-dim)',
    marginTop: 20,
    letterSpacing: '0.03em',
    lineHeight: 1.6,
  },
};

export default AuthScreen;
